/**
 * Trip CO₂ Calculator — page route at `/travel-impact-calculator`.
 *
 * Public, no-auth. The Jody header is shown on this page
 * intentionally — the calculator's purpose is to funnel visitors
 * into Jody signup (originally the waitlist; flipped when the
 * waitlist sunset, Aug 2026).
 *
 * Layout follows PRD §5.4: inputs → map hero → chart → plan-B link →
 * perspective stat → Meet Jody CTA. The map + result sections only
 * mount once a calculation has succeeded so partial UI never appears.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useToast } from '../hooks/useToast';
import { analytics } from '../shared/analytics';
import InputsPanel from '../features/tripCalculator/InputsPanel';
import type { CityResult } from '../features/geocoding/api';
import {
  getDefaultConfig,
  type ConfigByMode,
  type TransportMode,
} from '../features/tripCalculator/modes';
import {
  buildComparison,
  buildSegmentList,
  type ChartRow,
} from '../features/tripCalculator/buildComparison';
import {
  calculateBatch,
  TripCalculatorApiError,
} from '../features/tripCalculator/api';
import ResultChart from '../features/tripCalculator/ResultChart';
import MethodologyDialog from '../features/tripCalculator/MethodologyDialog';
import MapHero from '../features/tripCalculator/MapHero';
import ComparisonLeadIn from '../features/tripCalculator/ComparisonLeadIn';
import PerspectiveSection from '../features/tripCalculator/PerspectiveSection';
import CantTrainDialog from '../features/tripCalculator/CantTrainDialog';
import MeetJodyCTA from '../features/tripCalculator/MeetJodyCTA';
import QuotaExceededCard from '../features/tripCalculator/QuotaExceededCard';
import { withTestBypass } from '../features/tripCalculator/testBypass';
import ShareButton from '../features/tripCalculator/ShareButton';
import {
  buildShareUrl,
  decodeShareParams,
  formatShareText,
  isSameTrip,
} from '../features/tripCalculator/shareUrl';
import LoadingSpinner from '../components/LoadingSpinner';
import TripSummaryCard from '../features/tripCalculator/TripSummaryCard';
import './TripCalculatorPage.css';
import { useDocumentTitle } from '../shared/useDocumentTitle';

/**
 * Copy for the click-on-muted-Calculate-button hint. Brand-aligned
 * ("travel"), demonstrative ("this") anchors what's being referenced,
 * action-led ("Edit"), and "to recalculate" is more elegant than
 * "before recalculating". Auto-clears after `HINT_AUTO_CLEAR_MS`.
 */
const RECALCULATE_HINT = 'Edit this travel to recalculate.';
const HINT_AUTO_CLEAR_MS = 4000;

/**
 * Document title of the browser tab. scripts/write-page-html.js puts the
 * same title in this page's own HTML, for search results. Format:
 *   <primary keyword>: <descriptor> — <brand>
 * SEO best practice: keyword first, brand at the end, ~57 chars total
 * (Google trims around 60). Centralised so a copy tweak is one edit.
 */
const PAGE_TITLE = 'Travel Impact Calculator: Train, Plane, Car & More | Jody';

const TripCalculatorPage: React.FC = () => {
  useDocumentTitle('Travel Impact Calculator');
  const { showToast } = useToast();

  // Set the browser tab title on mount. CRA's `public/index.html`
  // ships a generic global default; we override per page so search
  // engines and tab strips see the right copy for /travel-impact-calculator.
  useEffect(() => {
    const previous = document.title;
    document.title = PAGE_TITLE;
    return () => {
      document.title = previous;
    };
  }, []);

  // ─────────── Inputs ───────────
  const [origin, setOrigin] = useState<CityResult | null>(null);
  const [destination, setDestination] = useState<CityResult | null>(null);
  // No default mode — the dropdown opens with a placeholder so the
  // user must make an explicit choice before Calculate enables.
  const [mode, setMode] = useState<TransportMode | null>(null);
  const [config, setConfig] = useState<ConfigByMode[TransportMode] | null>(null);
  const [roundTrip, setRoundTrip] = useState<boolean>(true);

  // ─────────── Async state ───────────
  const [isCalculating, setIsCalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<ChartRow[] | null>(null);
  /**
   * Set when the public batch endpoint returns 429 with code
   * `quota_exceeded`. Holds the absolute timestamp (ms) at which the
   * per-IP daily quota will reset, so the QuotaExceededCard can render
   * a concrete "your counter resets at HH:MM" string instead of a vague
   * "try again later." Cleared on any subsequent successful calculation.
   */
  const [quotaResetAt, setQuotaResetAt] = useState<number | null>(null);
  /**
   * Snapshot of (origin, destination) at the moment of the last
   * successful calculation. We keep this separate from the live
   * inputs so the result sections (map, perspective, CTA) keep
   * showing the *calculated* trip even if the user starts editing
   * the inputs for a new query.
   *
   * `config` is captured too so the perspective section can render
   * the "with N passengers in a petrol car" detail clause based on
   * the values that actually drove the calculation.
   */
  const [submittedTrip, setSubmittedTrip] = useState<{
    origin: CityResult;
    destination: CityResult;
    mode: TransportMode;
    config: ConfigByMode[TransportMode];
    roundTrip: boolean;
  } | null>(null);

  // Hint shown when the user clicks Calculate but the form already
  // matches the last-submitted (or URL-deeplinked) trip exactly.
  const [showRecalculateHint, setShowRecalculateHint] = useState(false);

  /**
   * Two-way toggle between the full inputs form and the collapsed
   * TripSummaryCard. Starts at 'form' so a first-time visitor sees
   * the inputs; flips to 'summary' automatically after a successful
   * calculation (see `runCalculation`) so the form gets out of the
   * way and the result region owns the page. The Edit button on the
   * summary card flips it back to 'form', and clicking the Cancel
   * button on the form returns to 'summary' without recalculating.
   */
  const [viewMode, setViewMode] = useState<'form' | 'summary'>('form');

  // ─────────── Derived ───────────
  /**
   * "Calculate is runnable" — used by InputsPanel to drive the
   * button's enabled/disabled/muted states. We fold the quota gate
   * in here (not as a separate `quotaExceeded` prop on InputsPanel)
   * because every consumer of "is calculate runnable" already treats
   * `false` the same way: button off, no submit. Adding a new prop
   * would force every state branch in InputsPanel to know about an
   * orthogonal reason without any behavioural difference. PRD §5.9:
   * when the gate is up, the button must not invite further clicks.
   */
  const canCalculate = useMemo(
    () =>
      Boolean(origin && destination && origin.id !== destination.id) &&
      mode !== null &&
      config !== null &&
      quotaResetAt === null,
    [origin, destination, mode, config, quotaResetAt]
  );

  const chosenRow = useMemo(() => rows?.find((r) => r.isChosen) ?? null, [rows]);

  /**
   * True iff the current form values match what's already on screen.
   * Drives the "muted" state of the Calculate button + the inline
   * "Edit this travel to recalculate" hint. Naturally covers both
   * "user just submitted" and "page loaded from a shared URL" since
   * both paths populate `submittedTrip` to match the inputs.
   */
  const unchangedSinceSubmit = useMemo(() => {
    if (!submittedTrip || !origin || !destination || !mode || !config) return false;
    return isSameTrip(
      { origin, destination, mode, config, roundTrip },
      submittedTrip
    );
  }, [origin, destination, mode, config, roundTrip, submittedTrip]);

  // Auto-clear the hint when the user edits anything (the condition
  // that made the hint relevant — "unchanged" — has gone away).
  useEffect(() => {
    if (!unchangedSinceSubmit && showRecalculateHint) {
      setShowRecalculateHint(false);
    }
  }, [unchangedSinceSubmit, showRecalculateHint]);

  // Belt-and-braces timeout in case the user stops interacting; we
  // don't want the hint to sit on screen forever.
  useEffect(() => {
    if (!showRecalculateHint) return;
    const t = window.setTimeout(
      () => setShowRecalculateHint(false),
      HINT_AUTO_CLEAR_MS
    );
    return () => window.clearTimeout(t);
  }, [showRecalculateHint]);

  /**
   * Trip-context properties attached to every trip-relevant Mixpanel
   * event (`share_*`, `signup_click`). Built once from the
   * submitted trip and chosen row so the analytics layer stays
   * consistent across surfaces: a signup click from the Meet Jody
   * CTA, the "Can't take trains?" dialog, and the quota-gate banner
   * all attribute to the same trip when the user does have one on
   * screen. Returns `null` when no trip has been submitted yet (e.g.
   * before the first calc, or after a deeplink-refresh-into-quota);
   * callers pass an empty object in that case.
   *
   * `distanceKm` and `co2eKg` are reported as **one-way** values, even
   * when the user picked round-trip. The chart row carries the RT-
   * doubled values (so the UI matches what the user calculated), but
   * analytics events keep modifiers separate from base units — the
   * `roundTrip: true` flag in the event tells the analyst to fold
   * 2× if they want the total. Without this, RT calcs would show as
   * ~2× the "trip distance" the user has in mind (e.g. Berlin↔Rome
   * bus RT would log ~3,068 km instead of the ~1,534 km road
   * one-way), making cross-trip averages misleading.
   */
  const tripTrackingProps = useMemo(() => {
    if (!submittedTrip || !chosenRow) return null;
    const oneWayFactor = submittedTrip.roundTrip ? 0.5 : 1;
    return {
      origin: submittedTrip.origin.name,
      // `country` is nullable in `CityResult` (the geocoder doesn't
      // always return one); pass null through rather than coercing to
      // a string so Mixpanel can distinguish "unknown" from "Empty".
      originCountry: submittedTrip.origin.country,
      destination: submittedTrip.destination.name,
      destinationCountry: submittedTrip.destination.country,
      mode: submittedTrip.mode,
      roundTrip: submittedTrip.roundTrip,
      co2eKg: chosenRow.kg * oneWayFactor,
      distanceKm: chosenRow.distanceKm * oneWayFactor,
    };
  }, [submittedTrip, chosenRow]);

  /**
   * Share-payload pair: the URL recipients open + the descriptive text
   * that accompanies it (clipboard / Web Share API `text` field).
   * Both derive from the *submitted* trip, not the live inputs, so a
   * mid-edit copy doesn't share half-finished state.
   */
  const sharePayload = useMemo(() => {
    if (!submittedTrip || !rows) return null;
    return {
      url: buildShareUrl(submittedTrip),
      text: formatShareText({
        rows,
        userMode: submittedTrip.mode,
        originName: submittedTrip.origin.name,
        destinationName: submittedTrip.destination.name,
      }),
      // Reuses the unified trip-tracking shape so share events and
      // signup-click events report the same trip properties. Per-render
      // additions (e.g. `placement`) are layered on at the call site.
      trackingProps: tripTrackingProps ?? {},
    };
  }, [submittedTrip, rows, tripTrackingProps]);

  // ─────────── Handlers ───────────
  const handleModeChange = useCallback((next: TransportMode) => {
    setMode(next);
    // Reset config when mode changes — the previous mode's shape no
    // longer applies, and this also preserves the "default unless
    // user customises" semantics.
    setConfig(getDefaultConfig(next));
  }, []);

  /**
   * The actual calculation pipeline, parameterised explicitly. Same
   * code path serves both the manual Calculate button (current state)
   * and the deeplink useEffect below (URL-parsed state). State is
   * async, so the deeplink path can't rely on `setX` having landed
   * before kicking off the network call — pass values directly.
   */
  const runCalculation = useCallback(
    async (input: {
      origin: CityResult;
      destination: CityResult;
      mode: TransportMode;
      config: ConfigByMode[TransportMode];
      roundTrip: boolean;
    }) => {
      setIsCalculating(true);
      setError(null);
      setRows(null);
      // Clear the quota gate optimistically. If the call comes back
      // with another quota_exceeded the catch block will reinstate it
      // with a fresh reset time; if it succeeds we've correctly
      // dismissed the card the moment the user is back inside the
      // quota (which, in practice, only happens via ?test=1 bypass or
      // tomorrow's window).
      setQuotaResetAt(null);

      try {
        const segments = buildSegmentList({ mode: input.mode, config: input.config });
        const { results } = await calculateBatch({
          origin: { lat: input.origin.lat, lng: input.origin.lng },
          destination: { lat: input.destination.lat, lng: input.destination.lng },
          segments,
          // Analytics annotations — backend uses these to populate the
          // travel_impact_calculator_calculate Mixpanel event.
          userMode: input.mode,
          roundTrip: input.roundTrip,
          // City + country for the CalculatorUsage row. Country is
          // resolved to its full English name inside `calculateBatch`
          // via `Intl.DisplayNames`. See privacy policy Section 19.2.
          originCity: input.origin.name,
          originCountry: input.origin.country,
          destinationCity: input.destination.name,
          destinationCountry: input.destination.country,
        });
        const chartRows = buildComparison({
          userMode: input.mode,
          userConfig: input.config,
          roundTrip: input.roundTrip,
          results,
        });
        setRows(chartRows);
        setSubmittedTrip(input);
        // Collapse the inputs form into the elegant summary card so
        // the result region (map → lead-in → chart → perspective →
        // plan-B → share/methodology → meet jody) owns the page.
        // The user can re-expand by clicking Edit on the summary card.
        setViewMode('summary');

        // Fire the calculate event from the browser, where Mixpanel's
        // SDK auto-enriches it with the visitor's anonymous distinct_id,
        // GeoIP-derived city/country, OS/browser, and URL. See
        // `frontend/src/shared/analytics.ts` for the privacy note.
        //
        // `distanceKm` and `co2eKg` are reported one-way even when the
        // user picked round-trip: the chart row carries doubled values
        // (so the UI matches the user's choice), but analytics keeps
        // modifiers separate from base units — the `roundTrip` flag in
        // the event tells the analyst whether to fold 2×. Without
        // this, RT calcs would log a "trip distance" ~2× what the user
        // has in mind, making cross-trip averages misleading. Same
        // rule as `tripTrackingProps` below.
        const chosen = chartRows.find((r) => r.isChosen);
        if (chosen) {
          const oneWayFactor = input.roundTrip ? 0.5 : 1;
          analytics.trackTravelImpactCalculatorCalculate({
            origin: input.origin.name,
            originCountry: input.origin.country,
            destination: input.destination.name,
            destinationCountry: input.destination.country,
            mode: input.mode,
            roundTrip: input.roundTrip,
            distanceKm: chosen.distanceKm * oneWayFactor,
            co2eKg: chosen.kg * oneWayFactor,
            segmentCount: chartRows.length,
          });
        }
        // Sync the address bar with the calculated trip so the URL
        // always reflects what's on screen, same URL the Share button
        // would generate. `replaceState` (not `pushState`) keeps the
        // back button from cluttering with intermediate calculations.
        //
        // `withTestBypass` preserves `?test=1` when present: a QA
        // session that landed on the page with the flag should stay
        // in test mode across subsequent calcs, not silently drop
        // back into the rate-limited flow on the first replaceState.
        try {
          window.history.replaceState({}, '', withTestBypass(buildShareUrl(input)));
        } catch {
          // Defensive: some browsers throw if called too frequently
          // (Safari). The page works fine without the URL sync.
        }
      } catch (err) {
        // 429s are soft failures and shouldn't disturb a result that's
        // already on screen. The page's `error` state stays reserved
        // for hard failures (400, 500, network).
        //
        // Two distinct UX surfaces by code:
        //   • `quota_exceeded` (daily cap): persistent in-page card
        //     with the signup CTA. The visitor has run 20 calcs
        //     today, so they're the calculator's highest-intent
        //     traffic; an auto-dismissing toast wastes that moment.
        //   • `rate_limited` (2-second floor): transient toast. It's a
        //     "you clicked too fast" hiccup, and a toast is the right
        //     surface for transient feedback.
        if (err instanceof TripCalculatorApiError && err.status === 429) {
          if (err.code === 'quota_exceeded') {
            // Backend computes retryAfterSeconds from the active
            // window's resetTime. Fall back to 24h if missing so the
            // card always has *something* to show.
            const retryAfter = err.retryAfterSeconds ?? 24 * 60 * 60;
            setQuotaResetAt(Date.now() + retryAfter * 1000);
            // Fire the gate-hit event from the browser so Mixpanel
            // attaches the visitor's auto-properties (city, OS, etc.),
            // letting the funnel slice quota-gated visitors by geo
            // and device. The server-side fire would have been an
            // empty event with `distinct_id: 'anonymous'`.
            analytics.trackTravelImpactCalculatorQuotaGateHit({
              retryAfterSeconds: retryAfter,
            });
            return;
          }
          if (err.code === 'rate_limited') {
            showToast({
              type: 'info',
              title: 'Slow down a moment',
              message: 'Calculations are limited to one every 2 seconds.',
              duration: 3000,
            });
            return;
          }
        }

        // Anything else — including unexpected 429 codes — falls through
        // to the page-level error bar as before.
        const message =
          err instanceof TripCalculatorApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Something went wrong. Please try again.';
        setError(message);
      } finally {
        setIsCalculating(false);
      }
    },
    [showToast]
  );

  const handleCalculate = useCallback(() => {
    if (!origin || !destination || !mode || !config) return;
    // If the form already matches what's on screen, the button has
    // been rendered "muted" (clickable for feedback). Show the hint
    // instead of running a redundant calculation.
    if (unchangedSinceSubmit) {
      setShowRecalculateHint(true);
      return;
    }
    return runCalculation({ origin, destination, mode, config, roundTrip });
  }, [
    origin,
    destination,
    mode,
    config,
    roundTrip,
    unchangedSinceSubmit,
    runCalculation,
  ]);

  // ─────────── Deeplink ───────────
  // On first mount, parse the URL's query string for a shared trip
  // and auto-calculate. Silently no-ops if params are missing or
  // malformed — the user just sees the empty inputs panel, no error
  // toast (PRD §5.10 deeplink behaviour).
  useEffect(() => {
    const parsed = decodeShareParams(new URLSearchParams(window.location.search));
    if (!parsed) return;

    setOrigin(parsed.origin);
    setDestination(parsed.destination);
    setMode(parsed.mode);
    setConfig(parsed.config);
    setRoundTrip(parsed.roundTrip);
    runCalculation(parsed);
    // Run exactly once on mount; subsequent input edits go through
    // the manual Calculate path.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─────────── Render ───────────
  const hasResults = Boolean(rows && submittedTrip && chosenRow);

  return (
    <main
      className="trip-calc-page"
      style={{ maxWidth: 960, margin: '0 auto', padding: '2rem 1rem' }}
    >
      <header style={{ marginBottom: '1.5rem' }}>
        <h1
          style={{
            fontSize: '1.75rem',
            marginBottom: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '0.625rem',
          }}
        >
          <span>Travel Impact Calculator</span>
          <span
            aria-label="Beta version"
            style={{
              fontSize: '0.6875rem',
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--brand-700, #047857)',
              background: 'rgba(5, 150, 105, 0.10)',
              border: '1px solid rgba(5, 150, 105, 0.25)',
              padding: '2px 8px',
              borderRadius: 999,
              lineHeight: 1.4,
              position: 'relative',
              top: '-2px',
            }}
          >
            Beta
          </span>
        </h1>
        <ul
          style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            color: 'var(--text-muted, #475569)',
            display: 'grid',
            gap: 4,
            fontSize: 14,
            lineHeight: 1.5,
          }}
        >
          <li style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <span aria-hidden style={{ flex: '0 0 auto' }}>🌍</span>
            <span>
              Understand how much climate impact is behind the transport of your next
              trip.
            </span>
          </li>
        </ul>
      </header>

      {/*
        Form ↔ summary toggle. In summary mode the form is hidden
        entirely — the trip selection moves onto the map as an
        overlay (TripSummaryCard, rendered inside the map wrapper
        further down). The page area above the map collapses to
        nothing, the map slides up, and the user sees pills floating
        on the map's top edge. Clicking Edit on the overlay flips
        back to 'form' and the InputsPanel re-renders here.
      */}
      {viewMode === 'form' && (
        <InputsPanel
          origin={origin}
          destination={destination}
          mode={mode}
          config={config}
          roundTrip={roundTrip}
          isCalculating={isCalculating}
          canCalculate={canCalculate}
          unchangedSinceSubmit={unchangedSinceSubmit}
          calculateHint={showRecalculateHint ? RECALCULATE_HINT : null}
          onOriginChange={setOrigin}
          onDestinationChange={setDestination}
          onModeChange={handleModeChange}
          onConfigChange={setConfig}
          onRoundTripChange={setRoundTrip}
          onCalculate={handleCalculate}
          onCancel={submittedTrip ? () => setViewMode('summary') : undefined}
        />
      )}

      {isCalculating && (
        <div style={{ marginTop: '1.5rem' }}>
          <LoadingSpinner message="Crunching numbers..." />
        </div>
      )}

      {/*
        Quota gate takes precedence over the generic error banner: a
        quota_exceeded 429 is the only "error" the calculator throws
        that's actually expected, conversion-relevant, and deserves
        its own surface. Everything else (4xx, 5xx, network) still
        falls through to the red banner.
      */}
      {quotaResetAt !== null ? (
        <>
          <QuotaExceededCard resetAt={quotaResetAt} />
          {/*
            Pair the banner with the existing Meet Jody CTA so the
            user has somewhere to actually convert. When a prior
            successful trip is still on screen, the same CTA also
            renders inside the result section below, but rendering it
            here too is intentional: the result section can be far
            down the page, and after a deeplink-refresh into a
            quota state there is no result section at all.
          */}
          <MeetJodyCTA
            originName={submittedTrip?.origin.name}
            destinationName={submittedTrip?.destination.name}
            source="travel_impact_calculator_quota_gate"
            trackingProps={tripTrackingProps ?? undefined}
          />
        </>
      ) : (
        error && (
          <div
            role="alert"
            style={{
              marginTop: '1rem',
              padding: '0.75rem 1rem',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: 8,
              color: '#991b1b',
            }}
          >
            {error}
          </div>
        )
      )}

      {hasResults && submittedTrip && chosenRow && rows && (
        <>
          {/* Map + (optionally) the trip-summary overlay. The wrapper
              is `position: relative` so TripSummaryCard's absolutely-
              positioned pills + Edit button anchor to this box rather
              than the page. In form mode the overlay is suppressed —
              there's already a full editable InputsPanel above. */}
          <div style={{ marginTop: '1.5rem', position: 'relative' }}>
            <MapHero
              origin={{ lat: submittedTrip.origin.lat, lng: submittedTrip.origin.lng }}
              destination={{
                lat: submittedTrip.destination.lat,
                lng: submittedTrip.destination.lng,
              }}
              originName={submittedTrip.origin.name}
              destinationName={submittedTrip.destination.name}
              // Push the route down when the trip-summary overlay is
              // taking the top edge of the map, so it doesn't sit
              // under the pills. ~50px covers a single-row chip
              // cluster; two-row vehicle-mode overlays still clear
              // the route because fitBounds keeps the route inside
              // the safe area (top + bottom padding), and pills
              // would only overlap with whitespace, not the line.
              topReserve={viewMode === 'summary' ? 50 : 0}
            />
            {viewMode === 'summary' && (
              <TripSummaryCard
                mode={submittedTrip.mode}
                config={submittedTrip.config}
                originName={submittedTrip.origin.name}
                destinationName={submittedTrip.destination.name}
                isRoundTrip={submittedTrip.roundTrip}
                onEdit={() => setViewMode('form')}
              />
            )}
          </div>

          <ComparisonLeadIn rows={rows} userMode={submittedTrip.mode} />

          {/* Results row: chart + perspective stat. Stacked on mobile;
              flips to a 2-column grid above 900px viewport width (see
              TripCalculatorPage.css) so a laptop reader gets both
              sections side-by-side without scrolling. The wrapper itself
              carries no top margin — on mobile each child <section>
              already declares its own; on desktop the CSS adds a single
              marginTop on the row instead. */}
          <div className="trip-calc-results-row">
            <ResultChart rows={rows} />

            <PerspectiveSection
              mode={submittedTrip.mode}
              config={submittedTrip.config}
              chosenKg={chosenRow.kg}
              originName={submittedTrip.origin.name}
              destinationName={submittedTrip.destination.name}
              isRoundTrip={submittedTrip.roundTrip}
            />
          </div>

          {/* Plan-B CTA sits between the perspective stat (which
              has just landed the emotional weight of the trip's
              impact) and the Meet Jody conversion card. Sequence:
              "here's the impact" → "here's an alternative path
              if trains aren't an option" → "sign up for Jody".
              Hidden for users who already chose train — pivoting
              from the celebration framing above to "but what if
              you can't take trains?" reads as undermining. */}
          {submittedTrip.mode !== 'train' && (
            <CantTrainDialog trackingProps={tripTrackingProps ?? undefined} />
          )}

          {/* Primary actions row: Share + Methodology, side-by-side as
              visual peers. Share is the filled CTA (intent: act on the
              result); Methodology is the neutral outlined button (intent:
              drill into the math). Placed just before the Meet Jody CTA
              so the conversion moment ("I've absorbed the impact, seen
              the plan-B framing") lands next to the act-on-it affordance. */}
          <div
            style={{
              marginTop: '1.25rem',
              display: 'flex',
              justifyContent: 'center',
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            {sharePayload && (
              <ShareButton
                variant="primary"
                shareUrl={sharePayload.url}
                shareText={sharePayload.text}
                trackingProps={{ ...sharePayload.trackingProps, placement: 'before_meet_jody' }}
              />
            )}
            <MethodologyDialog rows={rows} />
          </div>

          {/*
            Skip the bottom Meet Jody CTA when the quota gate is up:
            we already render one immediately under the gate banner
            (much closer to the user's eye on a quota_exceeded event),
            and two copies on the same page would feel like a bug.
          */}
          {quotaResetAt === null && (
            <MeetJodyCTA
              originName={submittedTrip.origin.name}
              destinationName={submittedTrip.destination.name}
              trackingProps={tripTrackingProps ?? undefined}
            />
          )}

          {/* Bottom secondary-actions row (PRD §5.4 item 9).
              Compact share + back-to-top, side-by-side. Users who
              scrolled all the way through still get a second chance
              to share + a one-tap way to scroll back up. */}
          {sharePayload && (
            <div
              style={{
                marginTop: '1.5rem',
                display: 'flex',
                justifyContent: 'center',
                gap: 12,
                flexWrap: 'wrap',
              }}
            >
              <ShareButton
                variant="compact"
                shareUrl={sharePayload.url}
                shareText={sharePayload.text}
                trackingProps={{ ...sharePayload.trackingProps, placement: 'bottom_row' }}
              />
              <button
                type="button"
                onClick={() =>
                  window.scrollTo({ top: 0, behavior: 'smooth' })
                }
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '8px 14px',
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#0f172a',
                  background: 'transparent',
                  border: '1px solid #cbd5e1',
                  borderRadius: 999,
                  cursor: 'pointer',
                }}
              >
                <span aria-hidden>↑</span>
                <span>Back to top</span>
              </button>
            </div>
          )}
        </>
      )}
    </main>
  );
};

export default TripCalculatorPage;
